<script lang="ts" setup>
import {
  NEW_ID,
  TYPE_HERO,
  TYPE_INFO,
  TYPE_INFOGRAPHIC,
  TYPE_ACTIONS_INITIATIVES,
  TYPE_QUESTION,
} from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { Story } from '@/models/story.model';
import { useEvents, Event } from '@/services/events.composable';
import { StoriesActions, StoriesState } from '@/stores/stories.store';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import {
  DisplayType,
  StoryItem,
  categoryRules,
} from '@/models/story-item.model';
import HeroContentForm from '@/components/hero/HeroContentForm.vue';
import InfoContentForm from '@/components/info/InfoContentForm.vue';
import QuestionContentForm from '@/components/quiz/QuestionContentForm.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import InfoGraphicContentForm from '@/components/infographic/InfoGraphicContentForm.vue';
import ActionsInitiativesContentForm from '@/components/stories/ActionsInitiativesContentForm.vue';

const props = defineProps({
  data: {
    type: Object as PropType<{ type: string; story: Story; value?: StoryItem }>,
    required: true,
  },
});

const types = [
  {
    title: 'Action',
    value: 'action',
  },
  {
    title: 'Info',
    value: 'info',
  },
  {
    title: 'Initiative',
    value: 'initiative',
  },
  {
    title: 'InfoGraphic',
    value: 'info-graphic',
  },
  {
    title: 'Hero',
    value: 'hero',
  },
  {
    title: 'Quiz Question',
    value: 'question',
  },
];

const store = useStore<ShiftCMSState & { ts: StoriesState }>();
const events = useEvents();

const valid = ref<boolean>(false);

const type = ref<string>(props.data.type);

const lastItem = props.data.story.items.length
  ? props.data.story.items[props.data.story.items.length - 1]
  : null;

const parentItem = lastItem;

const column = lastItem ? lastItem.column + 1 : 0;

const model = ref<StoryItem>({
  id: NEW_ID,
  story: props.data.story,
  parentItem,
  column,
  row: 0,
  displayType: DisplayType.Normal,
  actions: [],
  initiatives: [],
});

const action = computed<string>(() =>
  model.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveStoryItem = async () => {
  let success: boolean = false;

  if (valid.value) {
    if (model.value.id === NEW_ID) {
      success = await store.dispatch(StoriesActions.AddStoryItem, model.value);
    } else {
      success = await store.dispatch(
        StoriesActions.UpdateStoryItem,
        model.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved story item',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(StoriesActions.GetStories);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save story item',
      type: 'error',
    });
  }
};

const updateStoryContent = async () => {
  if (model.value.story === null && props.data.story) {
    Object.assign(model.value, { story: props.data.story });
  }
  if (props.data.value) {
    Object.assign(model.value, { ...props.data.value });
  }

  if (model.value.id === NEW_ID) {
    delete model.value.info;
    delete model.value.infoGraphic;
    delete model.value.question;
    delete model.value.hero;

    switch (type.value) {
      case TYPE_INFOGRAPHIC:
        Object.assign(model.value, {
          infoGraphic: { id: NEW_ID, title: '', description: '' },
        });
        break;
      case TYPE_INFO:
        Object.assign(model.value, {
          info: { id: NEW_ID, title: '', description: '' },
        });
        break;
      case TYPE_HERO:
        Object.assign(model.value, { hero: { id: NEW_ID, title: '' } });
        break;
      case TYPE_QUESTION:
        Object.assign(model.value, {
          question: { id: NEW_ID, title: '', answers: [] },
        });
        break;
      case TYPE_ACTIONS_INITIATIVES:
        Object.assign(model.value, {
          initiatives: [],
          actions: [],
        });
        break;
    }
  } else {
    const translated = await store.dispatch(
      StoriesActions.GetStoryItem,
      model.value.id,
    );
    Object.assign(model, translated);
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateStoryContent);

  updateStoryContent();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateStoryContent);
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveStoryItem">
    <v-container>
      <h2>{{ $t(action) }} {{ type }} <CurrentLanguage /></h2>

      <v-select
        v-model="type"
        :items="types"
        :rules="categoryRules"
        @update:modelValue="updateStoryContent"
        label="Type"
        required
        v-show="model.id === NEW_ID"
      ></v-select>

      <InfoGraphicContentForm
        v-if="type === TYPE_INFOGRAPHIC && model.infoGraphic"
        v-model="model.infoGraphic"
      />
      <InfoContentForm
        v-if="type === TYPE_INFO && model.info"
        v-model="model.info"
      />
      <HeroContentForm
        v-if="type === TYPE_HERO && model.hero"
        v-model="model.hero"
      />
      <QuestionContentForm
        v-if="type === TYPE_QUESTION && model.question"
        v-model="model.question"
      />
      <ActionsInitiativesContentForm
        v-if="type === TYPE_ACTIONS_INITIATIVES"
        v-model="model"
      />
      <!-- <ActionContentForm
        v-if="type === TYPE_ACTIONS_INITIATIVES && model.actions"
        v-model="model.actions"
      />
      <InitiativeContentForm
        v-if="type === TYPE_INITIATIVE && model.initiatives"
        v-model="model.initiatives"
      /> -->

      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
